import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity, Modal, TextInput, ScrollView, Switch } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { Redirect } from 'expo-router';
import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import { useAuth } from '../../src/context/AuthContext';
import InlineMessage from '../../src/components/InlineMessage';
import ResetPasswordModal from '../../src/components/ResetPasswordModal';
import AdminPermissionsModal from '../../src/components/AdminPermissionsModal';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive, rs, fs } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { useModalBottomPadding } from '../../src/utils/useModalBottomPadding';
import { titleCase } from '../../src/utils/format';

interface AdminItem {
  id: string; email: string; name: string; role: string;
  isActive: boolean; createdAt: string;
}

/** AdminAdmins - list, create, edit, and reset passwords for admin accounts. */
export default function AdminAdmins() {
  const { user } = useAuth();
  if (user?.adminRole !== 'super_admin') return <Redirect href="/admin/users" />;
  const { colors } = useTheme();
  const { width } = useResponsive();
  const isMobile = width < 768;
  const styles = useStyles(colors, isMobile);
  const modalBottomPadding = useModalBottomPadding();
  const [items, setItems] = useState<AdminItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [creating, setCreating] = useState(false);
  // Edit modal
  const [editItem, setEditItem] = useState<AdminItem | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [saving, setSaving] = useState(false);
  // Reset password modal
  const [resetTarget, setResetTarget] = useState<AdminItem | null>(null);
  // Permissions modal
  const [permsTarget, setPermsTarget] = useState<AdminItem | null>(null);
  // Inline message
  const [inlineMsg, setInlineMsg] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const fetchData = useCallback(async (p = 1, s = '', signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (s) params.set('search', s);
      const data = await api<{ admins: AdminItem[]; total: number }>(`/api/admin/admins?${params}`);
      if (signal?.aborted) return;
      setItems(data.admins);
      setTotal(data.total);
      setPage(p);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, []);

  useEffect(() => { const ac = new AbortController(); fetchData(1, '', ac.signal); return () => ac.abort(); }, []);

  const handleCreate = async () => {
    if (!createName || !createEmail || !createPassword) {
      setInlineMsg({ type: 'error', message: 'Name, email, and password are required' });
      return;
    }
    setCreating(true);
    try {
      await api('/api/admin/admins', {
        method: 'POST',
        body: { email: createEmail, password: createPassword, name: createName },
      });
      setShowCreate(false);
      setCreateName(''); setCreateEmail(''); setCreatePassword('');
      setInlineMsg({ type: 'success', message: 'Admin created' });
      fetchData(1, search);
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setCreating(false); }
  };

  const openEdit = (item: AdminItem) => {
    setEditItem(item);
    setEditName(item.name);
    setEditEmail(item.email);
    setEditActive(item.isActive);
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      await api(`/api/admin/admins/${editItem.id}`, {
        method: 'PUT',
        body: { name: editName, email: editEmail, isActive: editActive },
      });
      setEditItem(null);
      fetchData(page, search);
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setSaving(false); }
  };

  const handleResetPassword = async (password: string) => {
    if (!resetTarget) return;
    await api(`/api/admin/admins/${resetTarget.id}/reset-password`, {
      method: 'POST',
      body: { password },
    });
  };

  const renderItem = ({ item }: { item: AdminItem }) => (
    <View style={styles.rowWrapper}>
      <View style={styles.row}>
        <View style={styles.avatar}>
          <Ionicons name="shield" size={18} color={colors.primary} />
        </View>
        <View style={styles.rowBody}>
          <View style={styles.rowHeader}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <Text style={styles.name}>{titleCase(item.name)}</Text>
              {!item.isActive && (
                <View style={{ backgroundColor: colors.danger, borderRadius: 4, paddingHorizontal: 6, paddingVertical: 1 }}>
                  <Text style={{ fontSize: fs(10), color: colors.white, fontWeight: '700' }}>Inactive</Text>
                </View>
              )}
            </View>
            <Text style={styles.date}>{new Date(item.createdAt).toLocaleDateString()}</Text>
          </View>
          <Text style={styles.meta} numberOfLines={1}>{item.email} · {item.role}</Text>
        </View>
        <View style={styles.rowActions}>
          <TouchableOpacity onPress={() => openEdit(item)} hitSlop={8}>
            <Ionicons name="create-outline" size={18} color={colors.primary} />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setPermsTarget(item)} hitSlop={8} accessibilityLabel="Edit permissions">
            <Ionicons name="lock-closed-outline" size={18} color={colors.primary} />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setResetTarget(item)} hitSlop={8}>
            <Ionicons name="key-outline" size={18} color={colors.primary} />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      <TouchableOpacity style={styles.createBtn} onPress={() => setShowCreate(true)}>
        <Ionicons name="add" size={20} color={colors.white} />
        <Text style={styles.createText}>Create Admin</Text>
      </TouchableOpacity>

      <InlineMessage
        type={inlineMsg?.type || 'error'}
        message={inlineMsg?.message || ''}
        visible={!!inlineMsg}
        onDismiss={() => setInlineMsg(null)}
      />

      <AdminToolbar
        search={search} onSearchChange={setSearch} onSearch={() => fetchData(1, search)}
        total={total} page={page} limit={20} onPageChange={(p) => fetchData(p, search)}
        csvEndpoint={`/api/admin/admins?${new URLSearchParams({ ...(search && { search }) })}`}
        csvFilename="admins.csv" label="admin"
      />
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />} data={items} keyExtractor={item => item.id} renderItem={renderItem}
          ListEmptyComponent={<Text style={styles.empty}>No admins found</Text>}
        />
      )}

      {/* Create Admin Modal */}
      <Modal visible={showCreate} transparent animationType="slide" onRequestClose={() => setShowCreate(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>Create Admin</Text>
            <ScrollView nestedScrollEnabled>
              <Text style={styles.fieldLabel}>Name *</Text>
              <TextInput style={styles.fieldInput} placeholder="Full name" placeholderTextColor={colors.textLight}
                value={createName} onChangeText={setCreateName} maxLength={32} />
              <Text style={styles.fieldLabel}>Email *</Text>
              <TextInput style={styles.fieldInput} placeholder="Email" placeholderTextColor={colors.textLight}
                value={createEmail} onChangeText={setCreateEmail} keyboardType="email-address" autoCapitalize="none" maxLength={255} />
              <Text style={styles.fieldLabel}>Password *</Text>
              <TextInput style={styles.fieldInput} placeholder="Password" placeholderTextColor={colors.textLight}
                value={createPassword} onChangeText={setCreatePassword} secureTextEntry maxLength={128} />
            </ScrollView>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setShowCreate(false)}>
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleCreate} disabled={creating}>
                <Text style={styles.saveText}>{creating ? 'Creating...' : 'Create'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Edit Admin Modal */}
      <Modal visible={!!editItem} transparent animationType="slide" onRequestClose={() => setEditItem(null)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>Edit Admin</Text>
            <ScrollView nestedScrollEnabled>
              <Text style={styles.fieldLabel}>Name</Text>
              <TextInput style={styles.fieldInput} value={editName} onChangeText={setEditName} maxLength={32} />
              <Text style={styles.fieldLabel}>Email</Text>
              <TextInput style={styles.fieldInput} value={editEmail} onChangeText={setEditEmail} keyboardType="email-address" autoCapitalize="none" maxLength={255} />
              <Text style={styles.fieldLabel}>Active</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: 4 }}>
                <Switch value={editActive} onValueChange={setEditActive} trackColor={{ true: colors.primary }} />
                <Text style={{ color: colors.text, fontSize: fontSize.sm }}>{editActive ? 'Active' : 'Inactive'}</Text>
              </View>
            </ScrollView>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setEditItem(null)}>
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleSaveEdit} disabled={saving}>
                <Text style={styles.saveText}>{saving ? 'Saving...' : 'Save'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Reset Password Modal */}
      <ResetPasswordModal
        visible={!!resetTarget}
        onClose={() => setResetTarget(null)}
        onSubmit={handleResetPassword}
        targetName={titleCase(resetTarget?.name) || ''}
      />

      {/* Permissions Modal */}
      {permsTarget && (
        <AdminPermissionsModal
          visible={true}
          adminId={permsTarget.id}
          adminName={titleCase(permsTarget.name)}
          adminEmail={permsTarget.email}
          adminRole={permsTarget.role}
          onClose={() => setPermsTarget(null)}
          onSaved={() => setInlineMsg({ type: 'success', message: 'Permissions updated.' })}
        />
      )}
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  createBtn: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-end', gap: 4, backgroundColor: c.primary, borderRadius: borderRadius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, marginBottom: spacing.sm },
  createText: { fontSize: fontSize.sm, color: c.white, fontWeight: fontWeight.semibold },
  rowWrapper: { borderBottomWidth: 1, borderBottomColor: c.borderLight, paddingVertical: 10 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  avatar: { width: rs(36), height: rs(36), borderRadius: rs(18), backgroundColor: c.primaryBg, justifyContent: 'center', alignItems: 'center' },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  name: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  date: { fontSize: fontSize.xs, color: c.textLight },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  rowActions: { flexDirection: 'row', gap: 12 },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: isMobile ? 'flex-end' : 'center', alignItems: 'center', padding: isMobile ? 0 : spacing.lg },
  modalContent: { backgroundColor: c.surface, borderRadius: isMobile ? 0 : borderRadius.lg, borderTopLeftRadius: borderRadius.lg, borderTopRightRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: isMobile ? undefined : 420 },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  fieldLabel: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: spacing.sm, textTransform: 'uppercase' },
  fieldInput: { backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm, fontSize: fontSize.sm, color: c.text, marginTop: 4, borderWidth: 1, borderColor: c.border },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.md },
  cancelBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm },
  cancelText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
  saveBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm, backgroundColor: c.primary },
  saveText: { fontSize: fontSize.sm, color: c.white, fontWeight: fontWeight.semibold },
});

const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
